import { useEffect, useRef } from 'react'

/** Reads the element id from the current URL hash, e.g. `#sure-100` → `sure-100`. */
const getHashTargetId = (): string => {
  if (typeof window === 'undefined') return ''
  return decodeURIComponent(window.location.hash.replace(/^#/, ''))
}

/**
 * Scrolls to the element named in the URL hash once the reader content is on
 * screen. The browser's own hash scrolling cannot do this reliably because the
 * Quran text is often rendered after the navigation (native bridge, offline
 * data, saved reading mode), so the target does not exist yet at that moment.
 *
 * Each page + hash pair is scrolled to only once, so later re-renders do not
 * pull the reader back while they are reading.
 *
 * @param isContentReady Whether the page text has been rendered.
 * @param contentKey Changes whenever the rendered content changes (page, reading mode).
 */
export const useScrollToHashTarget = (isContentReady: boolean, contentKey: string) => {
  const lastScrolledTargetRef = useRef('')

  useEffect(() => {
    if (!isContentReady) return

    const targetId = getHashTargetId()
    if (!targetId) return

    const targetKey = `${window.location.pathname}${window.location.search}#${targetId}`
    if (lastScrolledTargetRef.current === targetKey) return

    // Some reading modes (e.g. the Hayrat page image) have no surah headers.
    const targetElement = document.getElementById(targetId)
    if (!targetElement) return

    targetElement.scrollIntoView({ block: 'start' })
    lastScrolledTargetRef.current = targetKey
  }, [isContentReady, contentKey])
}
